/**
 * Material news feed — surfaces Claude-scored news for the provided
 * symbol set, default-filtered to HIGH + MEDIUM materiality (drops the
 * Motley-Fool / generic-recap noise). Toggle flips between Material
 * default and All.
 *
 * Steve directive 2026-05-23: production placement decision after the
 * /news-ab calibration. Reads /api/news-ab (which already joins all
 * scope symbols), filters client-side to the symbol prop, sorts by
 * publishedDate desc.
 *
 * Mounted on /portfolio (portfolio symbols) and /alpha-zone (AZ symbols)
 * as a dedicated section above the existing PortfolioEventsFeed overlay.
 * Once the keyword feed sunsets (post 1-2 week eyeball), the overlay
 * goes too.
 */
'use client';

import { useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';

interface ScoredArticle {
  id: string;
  publishedDate: string;
  publisher: string | null;
  site: string | null;
  title: string;
  text: string;
  url: string;
  sentiment: number | null;
  sentimentLabel: string | null;
  materiality: 'low' | 'med' | 'high' | null;
  taxonomy: string[] | null;
  oneLineSummary?: string | null;
}

interface SymbolBundle {
  symbol: string;
  scope: ('held' | 'alpha-zone')[];
  scored: ScoredArticle[];
  aggregates: {
    keywordHits: number;
    scoredCount: number;
    scoredHigh: number;
    scoredMed: number;
    scoredLow: number;
    avgSentiment: number | null;
  };
}

interface AbResponse {
  asOf: string;
  windowDays: number;
  bundles: SymbolBundle[];
}

interface Props {
  /** Scope to filter to. Reads scope tags from /api/news-ab bundle output. */
  scope: 'held' | 'alpha-zone' | 'all';
  /** Section heading. */
  title?: string;
  /** Show the "vs keyword feed" calibration link to the /news-ab page. */
  showCalibrationLink?: boolean;
}

function fmtDate(ts: string): string {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return ts;
  const dow = d.toLocaleString('en-US', { weekday: 'short' });
  const mo = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${dow} ${mo} ${day} · ${hh}:${mm}`;
}

function sentimentTone(s: number | null): string {
  if (s == null) return 'text-[#64748b]';
  if (s >= 0.3) return 'text-emerald-300';
  if (s <= -0.3) return 'text-red-300';
  return 'text-amber-300';
}

function materialityTone(m: 'low' | 'med' | 'high' | null): string {
  if (m === 'high') return 'bg-red-500/15 text-red-300 border-red-500/30';
  if (m === 'med') return 'bg-amber-500/10 text-amber-300 border-amber-500/30';
  return 'bg-[#1e293b] text-[#94a3b8] border-[#334155]';
}

function inScope(bundle: SymbolBundle, scope: Props['scope']): boolean {
  if (scope === 'all') return true;
  return bundle.scope.includes(scope);
}

export function ScoredNewsFeed({ scope, title = 'Material News', showCalibrationLink = true }: Props) {
  const { data } = useResource<AbResponse>('/api/news-ab', 60_000);
  const [showLow, setShowLow] = useState(false);

  const articles = useMemo(() => {
    if (!data) return [];
    // Same article often appears under multiple symbols (FMP returns the
    // same URL when multiple tickers are mentioned). Dedupe by article id
    // but collect all associated in-scope symbols into a list so the row
    // shows multi-symbol attribution.
    const byId = new Map<string, ScoredArticle & { symbols: string[] }>();
    for (const b of data.bundles) {
      if (!inScope(b, scope)) continue;
      for (const a of b.scored) {
        if (a.sentiment == null) continue;
        if (!showLow && a.materiality === 'low') continue;
        const existing = byId.get(a.id);
        if (existing) {
          if (!existing.symbols.includes(b.symbol)) existing.symbols.push(b.symbol);
        } else {
          byId.set(a.id, { ...a, symbols: [b.symbol] });
        }
      }
    }
    const rows = [...byId.values()];
    rows.sort((a, b) => Date.parse(b.publishedDate) - Date.parse(a.publishedDate));
    return rows;
  }, [data, scope, showLow]);

  // Aggregate counts across the filtered scope
  const counts = useMemo(() => {
    let high = 0, med = 0, low = 0;
    if (!data) return { high, med, low };
    for (const b of data.bundles) {
      if (!inScope(b, scope)) continue;
      high += b.aggregates.scoredHigh;
      med += b.aggregates.scoredMed;
      low += b.aggregates.scoredLow;
    }
    return { high, med, low };
  }, [data, scope]);

  if (!data) {
    return (
      <section className="border border-[#1e293b] rounded-md bg-[#0d1117] p-4 text-[12px] text-[#64748b] italic">
        Loading scored news...
      </section>
    );
  }

  return (
    <section className="border border-[#1e293b] rounded-md bg-[#0d1117]">
      <header className="px-4 py-2 border-b border-[#1e293b] flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <h2 className="text-[#c9a84c] text-[12px] uppercase tracking-widest font-bold">{title}</h2>
          <span className="text-[10px] text-[#64748b]">
            <span className="text-red-300">{counts.high} hi</span>
            <span className="mx-1">·</span>
            <span className="text-amber-300">{counts.med} med</span>
            {counts.low > 0 && (
              <>
                <span className="mx-1">·</span>
                <span className="text-[#94a3b8]">{counts.low} low</span>
              </>
            )}
            <span className="text-[#475569] ml-2">trailing {data.windowDays}d</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowLow(s => !s)}
            className={`text-[10px] uppercase tracking-wider rounded-md px-2 py-1 border transition-colors ${
              showLow
                ? 'bg-[#1e293b] text-white border-[#334155]'
                : 'text-[#64748b] border-[#1e293b] hover:text-white hover:border-[#334155]'
            }`}
            title="Toggle low-materiality opinion / generic articles"
          >
            {showLow ? 'Showing all' : 'Material only'}
          </button>
          {showCalibrationLink && (
            <a
              href="/news-ab"
              className="text-[10px] uppercase tracking-wider text-[#64748b] hover:text-white border border-[#1e293b] hover:border-[#334155] rounded-md px-2 py-1"
              title="Open the keyword-vs-scored calibration page"
            >
              A/B ↗
            </a>
          )}
        </div>
      </header>
      <div className="divide-y divide-[#1e293b]">
        {articles.length === 0 && (
          <div className="text-[11px] text-[#64748b] italic px-4 py-6 text-center">
            {showLow
              ? 'No scored articles in trailing 7d.'
              : 'Nothing material today — quiet news week on the scope.'}
          </div>
        )}
        {articles.map(a => (
          <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-[#0a0e14] transition-colors">
            <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono font-bold text-white text-[12px]">{a.symbols.join(' · ')}</span>
                {a.materiality && (
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${materialityTone(a.materiality)}`}>
                    {a.materiality}
                  </span>
                )}
                {a.sentiment != null && (
                  <span className={`text-[10px] font-mono ${sentimentTone(a.sentiment)}`}>
                    {a.sentiment >= 0 ? '+' : ''}{a.sentiment.toFixed(2)}
                  </span>
                )}
              </div>
              <span className="text-[9px] text-[#64748b] font-mono">{fmtDate(a.publishedDate)}</span>
            </div>
            <div className="text-[12px] text-[#e2e8f0] leading-snug">{a.title}</div>
            {a.oneLineSummary && (
              <div className="text-[10px] text-[#94a3b8] italic mt-1 leading-snug">{a.oneLineSummary}</div>
            )}
            <div className="flex items-baseline justify-between gap-2 mt-1 flex-wrap">
              {a.publisher && <span className="text-[9px] text-[#64748b]">{a.publisher}</span>}
              {a.taxonomy && a.taxonomy.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {a.taxonomy.map(t => (
                    <span key={t} className="text-[9px] text-[#475569] uppercase tracking-wider">· {t}</span>
                  ))}
                </div>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
